/**
 * @author: Rejo Varghese
 * @description: This file contains the actions for the operator web config.
 */

import { GET_SIGNED_WEB_IMAGE_UPLOAD_URL, DELETE_WEB_IMAGE_FROM_BUCKET_URL, GET_OPERATOR_WEB_CONFIG_URL, UPDATE_OPERATOR_WEB_CONFIG_COLORS_URL, UPDATE_LOGOS_IN_TRAVELS_CONFIG_URL,
    UPDATE_WEB_ACTION_MAPPING_URL
 } from "../utils/api-url";
import { postCall } from "../utils/network.util";
import { getWebSiteUrlForStorage, SUCCESS_STATUS } from "../utils/constant";

const getSignedUploadUrlsForWebImages = (images) => {
    const webSiteUrl = getWebSiteUrlForStorage();
    return Promise.all(
        images.map(async ({ type, file }) => ({
            imageVariable: type,
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
            webSiteUrl
        }))
    ).then((requestImages) =>
        postCall(GET_SIGNED_WEB_IMAGE_UPLOAD_URL, { images: requestImages })
    ).then(response => {
        if (
            response.status === SUCCESS_STATUS &&
            response.payload &&
            response.payload.Status === SUCCESS_STATUS &&
            response.payload.Payload?.uploadUrls
        ) {
            // Expecting uploadUrls as an object map: { [fileName]: uploadUrl } (e.g. { "fleet-image1.webp": "https://..." })
            return response.payload.Payload.uploadUrls;
        }
        throw new Error(response?.payload?.Message || 'Failed to get upload URLs');
    });
};

const uploadFileToSignedUrl = (uploadUrl, file) => {
    return fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
            'Content-Type': file.type || 'application/octet-stream'
        }
    }).then((response) => {
        if (!response.ok) {
            throw new Error(`Upload failed: ${response.statusText}`);
        }
        return response;
    });
};

export const uploadWebImages = (images, onSuccess = () => {}, onFailure = () => {}) => {
    getSignedUploadUrlsForWebImages(images)
        .then((uploadUrlMap) => {
            const uploadPromises = images.map(({ type, file }) => {
                const uploadUrl = uploadUrlMap?.[file.name];
                if (!uploadUrl) {
                    throw new Error(`Missing upload URL for file: ${file.name}`);
                }
                return uploadFileToSignedUrl(uploadUrl, file);
            });
            return Promise.all(uploadPromises);
        })
        .then(() => onSuccess({ Status: SUCCESS_STATUS }))
        .catch(error => onFailure(error?.message || 'Upload failed'));
};

export const deleteWebImageFromBucket = (imageVariables, onSuccess = () => {}, onFailure = () => {}) => {
    const webSiteUrl = getWebSiteUrlForStorage();
    if (!webSiteUrl) {
        onFailure('Operator website not configured');
        return;
    }
    const variables = Array.isArray(imageVariables) ? imageVariables : [imageVariables];
    if (variables.length === 0) {
        onSuccess();
        return;
    }
    postCall(DELETE_WEB_IMAGE_FROM_BUCKET_URL, { webSiteUrl, imageVariables: variables })
        .then((response) => {
            if (response.status === SUCCESS_STATUS && response.payload?.Status === SUCCESS_STATUS) {
                onSuccess();
            } else {
                onFailure(response?.payload?.Message || 'Failed to delete image(s) from bucket');
            }
        })
        .catch((error) => onFailure(error?.message || 'Failed to delete image(s) from bucket'));
};

export const getWebConfig = (onSuccess = () => {}, onFailure = () => {}) => {
    return postCall(GET_OPERATOR_WEB_CONFIG_URL, {}).then(response => {
        if (response.status === SUCCESS_STATUS && response.payload.Status === SUCCESS_STATUS) {
            onSuccess(response.payload.Payload);
        } else {
            onFailure(response.payload.Message || 'Failed to get web config');
        }
    });
}

export const updateWebColors = (colors, onSuccess = () => {}, onFailure = () => {}) => {
    return postCall(UPDATE_OPERATOR_WEB_CONFIG_COLORS_URL, { colors }).then(response => {
        if (response.status === SUCCESS_STATUS && response.payload.Status === SUCCESS_STATUS) {
            onSuccess(response.payload.Payload);
        } else {
            onFailure(response.payload.Message || 'Failed to update web colors');
        }
    });
}


export const updateLogosInTravelsConfig = (logos, onSuccess = () => {}, onFailure = () => {}) => {
    return postCall(UPDATE_LOGOS_IN_TRAVELS_CONFIG_URL, { logos }).then(response => {
        if (response.status === SUCCESS_STATUS && response.payload.Status === SUCCESS_STATUS) {
            onSuccess(response.payload.Payload);
        } else {
            onFailure(response.payload.Message || 'Failed to update logos in travels config');
        }
    });
}

export const updateWebActionMapping = (actionMapping, onSuccess = () => {}, onFailure = () => {}) => {
    return postCall(UPDATE_WEB_ACTION_MAPPING_URL, { actionMapping }).then(response => {
        if (response.status === SUCCESS_STATUS && response.payload.Status === SUCCESS_STATUS) {
            onSuccess(response.payload.Payload);
        } else onFailure(response.payload.Message || 'Failed to update web action mapping');
    });
}